import React, { Component } from "react";
import { Col, Label, FormGroup, Row } from "reactstrap";
import { AppCard, StatusBadge, PageLayout } from "../../components/ui";
import { Merchant, MerchantAddresses, MerchantContact, MerchantDocument, MerchantStatus } from "../../models/MerchantModel";
import { Dropdown } from "primereact/dropdown";
import { SelectItem } from "../../models/SelectItem";
import { Button } from "primereact/button";
import history from "../../history";
import { toastService } from "../../services/toastService";
import _ from "lodash";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { clientApiService } from "../../services/api/clients/ClientApiService";
import { httpService } from "../../services/HttpService";
import ExtendedInputText from "../../components/ExtendedInputText";
import StringUtils from "../../utils/stringUtils";
import Loader from "../../components/Loader";
import { utils, countryCodeToFlag } from "../../utils/utils";
import { ProfileImage } from "../../components/Image/ProfileImage";
import MerchantContactComponent from "./MerchantContact";
import MerchantAddress from "./MerchantAddress";
import MerchantDocuments from "./MerchantDocuments";
import { CurrencyModel } from "../../models/CurrencyModel";
import { MultiSelect } from "primereact/multiselect";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import { Check, UserPlus } from "lucide-react";
import imageCompression from "browser-image-compression";
import { Image_File_Size, ALPHA_NUMERIC_REGEX, BULSTAT_LENGTH, LEADING_ZEROS_REGEX, NON_DIGIT_REPLACEMENT_REGEX } from "../../models/AppConstants";

interface MerchantDetailsState {
    isLoading: boolean;
    merchant: Merchant;
    currencies: SelectItem[];
    isSubmit: boolean;
    isDocumentInvalid: boolean;
}
export enum currencies {
    BGN = 1,
    USD = 2,
    EUR = 3,
    AAA = 4,
}
export default class CreateMerchantComponent extends Component<Record<string, never>, MerchantDetailsState> {
    countries: SelectItem[] = [];
    merchantClients: SelectItem[] = [];

    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    inputRef: { [key: string]: React.RefObject<HTMLInputElement> };

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            merchant: new Merchant(),
            isLoading: true,
            currencies: [],
            isSubmit: false,
            isDocumentInvalid: false,
        };

        this.inputRef = {
            logo: React.createRef(),
            name: React.createRef(),
            email: React.createRef(),
            websiteUrl: React.createRef(),
            phone: React.createRef(),
            vatNumber: React.createRef(),
            bulStat: React.createRef(),
            flexCubeBranch: React.createRef(),
            minimumTransactionAmount: React.createRef(),
        };
    }

    async componentDidMount() {
        this.setState({ isLoading: true });

        const countriesResult = await utils.getCountriesResult();
        if (!countriesResult.success) {
            toastService.error(countriesResult.message);
            this.setState({ isLoading: false });
            return;
        }

        this.countries = utils.getCountries();

        const clientsResult = await clientApiService.getClients();
        if (!clientsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(clientsResult.message);
            return;
        }
        this.merchantClients = clientsResult.data;

        const currenciesResult = await currenciesApiService.getActive();

        if (!currenciesResult.success) {
            this.setState({ isLoading: false });
            toastService.error(currenciesResult.message);
            return;
        }

        const currencies = currenciesResult.data.map((m: CurrencyModel) => ({ label: m.charCode, value: m.id }) as SelectItem);

        this.setState({ isLoading: false, currencies: currencies });
    }

    updateValue = (fieldName: string, value: string) => {
        const merchantDetails = this.state.merchant as any;

        merchantDetails[fieldName] = value;

        this.setState({ merchant: merchantDetails });
    };

    validateInput = (fieldName: string): boolean => {
        const merchantDetails = this.state.merchant as any;
        return this.state.isSubmit && (merchantDetails[fieldName] === "" || undefined) ? true : false;
    };

    validateDropdown = (fieldName: string) => {
        const merchantDetails = this.state.merchant as any;
        if (this.state.isSubmit && (merchantDetails[fieldName].length === 0 || undefined)) return "multiselect-custom p-invalid";
        else return "multiselect-custom";
    };


    onImageSelected = async (image: File) => {
        const merchantDetails = this.state.merchant;
        const allowedTypes = ["image/jpeg", "image/png"];
        if (!allowedTypes.includes(image.type)) {
            toastService.error("Only JPEG and PNG files are allowed.", "File Type");
            return;
        }

        if (image.size > Image_File_Size) {
            toastService.error(`Files less than ${Image_File_Size / (1024 * 1024)}MB are supported only.`, "File Size");
            return;
        }

        const compressedImage = await imageCompression(image, { maxSizeMB: Image_File_Size, useWebWorker: true });
        merchantDetails.logo = (await utils.convertFileToBase64(compressedImage)) as string;

        this.setState({ merchant: merchantDetails });
    };

    fileInput = (input: boolean) => {
        if (!input) {
            toastService.error("File Name Exceeds 128 Characters.", "File Name");
            return;
        }
    };

    countryOptionTemplate(option: SelectItem) {
        return (
            <div className="flex items-center gap-2">
                <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                <span>{option.label}</span>
            </div>
        );
    }

    onUpdateCurrencyMultiSelect = (ids: number[]) => {
        if (this.state.merchant != null) {
            const merchant = this.state.merchant;
            if (ids.includes(currencies.AAA)) {
                // Select all currency IDs

                merchant.merchantCurrencies = this.state.currencies
                    .filter((currency) => parseInt(currency.value) !== currencies.AAA)
                    .map((currency) => parseInt(currency.value));
            } else {
                merchant.merchantCurrencies = ids;
            }
            this.setState({ merchant: merchant });
        }
    };


    validateForm = () => {
        const { merchant } = this.state;

        if (!merchant.name || merchant.name.trim() === "") {
            return;
        }
        if (!merchant.email || !StringUtils.isValidEmail(merchant.email)) {
            return;
        }
        if (!merchant.websiteUrl || merchant.websiteUrl.trim() === "") {
            return;
        }
        if (!merchant.phone || merchant.phone.trim() === "") {
            return;
        }
        if (!merchant.flexCubeBranch || merchant.flexCubeBranch.trim() === "") {
            return;
        }
        if (!merchant.vatNumber || merchant.vatNumber.trim() === "") {
            return;
        }
        if (!merchant.bulStat || merchant.bulStat.trim() === "") {
            return;
        }
        if (!merchant.merchantCurrencies || merchant.merchantCurrencies.length === 0) {
            return;
        }

        return true;
    };

    onSubmitButtonClick = async () => {
        this.setState({ isSubmit: true });
        for (const key in this.inputRef) {
            const ref = this.inputRef[key];
            if (ref.current && utils.isNullOrEmptyString((this.state.merchant as any)[key])) {
                ref.current.focus();
                break;
            }
        }

        if (this.state.merchant.logo.length === 0 || this.state.merchant.merchantCurrencies.length === 0 || this.state.merchant.clientIds.length === 0) {
            this.inputRef.flexCubeBranch.current?.focus();
            return;
        }

        if (this.state.merchant.merchantContacts.length === 0) {
            toastService.error("Atleast one Primary Contact is required.");
            return;
        }

        if (this.state.merchant.merchantAddresses.length === 0) {
            toastService.error("Atleast one Corresspondance Addrees is required.");
            return;
        }


        if (this.state.merchant.merchantDocuments.length > 0) {
            const requiredFields: (keyof MerchantDocument)[] = ["fileName", "documentType", "fileData"];
            let hasEmptyFields = false;
            this.state.merchant.merchantDocuments.forEach((doc) => {
                requiredFields.forEach((field) => {
                    if (!doc[field]) {
                        hasEmptyFields = true;
                        return;
                    }
                });
            });
            if (hasEmptyFields) {
                this.setState({ isDocumentInvalid: true });
                toastService.error("Document information is not valid.");
                return;
            }
        }

        this.setState({ isLoading: true });
        const result = await merchantApiService.create(this.state.merchant!);

        this.setState({ isLoading: false });

        if (result.success) {
            history.push("/merchants");
        } else {
            toastService.error(result.message);
            return;
        }
    };

    onIntegrationSubmitButtonClick = async () => {
        const result = await merchantApiService.updateMerchantIntegration(this.state.merchant!);
        if (result.success) {
            history.push("/merchants");
        } else {
            toastService.error(result.message);
        }
    };

    setMerchantStatus = (status: MerchantStatus) => {
        const merchant = this.state.merchant;
        merchant!.status = status;
        this.setState({ merchant: merchant });
    };

    defaultImage = () => {
        return "";
    };

    onContactsUpdated = (contacts: MerchantContact[]) => {
        this.state.merchant!.merchantContacts = contacts;
    };

    onContactDeleted = (contact: MerchantContact) => {
        const index = this.state.merchant!.merchantContacts.findIndex(
            (f) => f.email == contact.email && f.name == contact.name && f.phone == contact.phone && f.type == contact.type,
        );

        if (index >= 0) {
            this.state.merchant!.merchantContacts.splice(index, 1);
        }
    };

    onDocumentsUpdated = (documents: MerchantDocument[]) => {
        this.state.merchant!.merchantDocuments = documents;
    };

    onAddressUpdated = (address: MerchantAddresses[]) => {
        this.state.merchant!.merchantAddresses = address;
    };

    ValidateDocuments = (value: boolean) => {
        this.setState({ isDocumentInvalid: value });
    }

    render() {
        return (
            <>
                {this.state.isLoading ? (
                    <Col md={12} lg={12}>
                        <AppCard className="min-h-[500px] flex items-center justify-center">
                            <Loader />
                        </AppCard>
                    </Col>
                ) : (
                    <PageLayout heading="Add New Merchant" icon={UserPlus}>
                        <div className="space-y-4">
                            <AppCard>
                                            <AppCard.Header title="Merchant Details" />
                                            <AppCard.Body>
                                                <Row>
                                                    <Col md="3" lg="3" sm="12" className="p-8 flex flex-col items-center">
                                                                    <div style={{ cursor: "pointer", width: 128 }}>
                                                                        <ProfileImage
                                                                            imageStyle={{ width: "100%" }}
                                                                            className="rounded-circle"
                                                                            src={
                                                                                this.state.merchant.logo == null || this.state.merchant.logo.length === 0
                                                                                    ? this.defaultImage()
                                                                                    : utils.appendBase64Data(this.state.merchant.logo)
                                                                            }
                                                                            alt="merchant logo"
                                                                            onImageSelected={this.onImageSelected}
                                                                            fileInput={this.fileInput}
                                                                        />
                                                                        {this.state.isSubmit && this.state.merchant.logo.length == 0 &&
                                                                            <span className="p-error block mt-2 text-center text-xs">Logo is required.</span>
                                                                        }
                                                                    </div>
                                                                </Col>
                                                                <Col md={9} lg={9} sm="12">
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>Name</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.name}
                                                                                    name="Name"
                                                                                    value={this.state.merchant.name}
                                                                                    required={this.validateInput("name")}
                                                                                    onChange={(value) => this.updateValue("name", value)}
                                                                                    onValidStatusChanged={(value: boolean) =>
                                                                                        this.validationErrors.set("name", value)
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>Email Address</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.email}
                                                                                    type="email"
                                                                                    value={this.state.merchant.email}
                                                                                    onChange={(value) => this.updateValue("email", value)}
                                                                                    validation={() =>
                                                                                        this.state.isSubmit && this.state.merchant.email === ""
                                                                                            ? false
                                                                                            : this.state.merchant.email === ""
                                                                                                ? true
                                                                                                : StringUtils.isValidEmail(this.state.merchant.email!)
                                                                                    }
                                                                                    name="Email"
                                                                                    onValidStatusChanged={(value: boolean) =>
                                                                                        this.validationErrors.set("email", value)
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row >
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>Website Url</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.websiteUrl}
                                                                                    required={this.validateInput("websiteUrl")}
                                                                                    value={this.state.merchant.websiteUrl}
                                                                                    onChange={(e) => this.updateValue("websiteUrl", e)}
                                                                                    name="WebsiteUrl"
                                                                                    onValidStatusChanged={(value: boolean) => {
                                                                                        this.validationErrors.set("websiteUrl", value);
                                                                                    }}
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>Phone</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.phone}
                                                                                    required={this.validateInput("phone")}
                                                                                    value={this.state.merchant.phone}
                                                                                    type="text"
                                                                                    onChange={(value) => this.updateValue("phone", value)}
                                                                                    name="Phone"
                                                                                    onValidStatusChanged={(value: boolean) => {
                                                                                        this.validationErrors.set("phone", value)
                                                                                    }
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>

                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>VAT Number</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.vatNumber}
                                                                                    required={this.validateInput("vatNumber")}
                                                                                    value={this.state.merchant.vatNumber}
                                                                                    type="text"
                                                                                    onChange={(value) => this.updateValue("vatNumber", value)}
                                                                                    name="VatNumber"
                                                                                    onValidStatusChanged={(value: boolean) =>
                                                                                        this.validationErrors.set("VatNumber", value)
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <Label>Currency</Label>
                                                                            <FormGroup>
                                                                                <MultiSelect
                                                                                    value={this.state.merchant.merchantCurrencies}
                                                                                    options={this.state.currencies}
                                                                                    onChange={(e) => this.onUpdateCurrencyMultiSelect(e.value)}
                                                                                    placeholder="Select Currency"
                                                                                    filter
                                                                                    style={{ width: "100%" }}
                                                                                    className={`${this.validateDropdown("merchantCurrencies")}`}
                                                                                />
                                                                                {this.state.isSubmit && this.state.merchant.merchantCurrencies.length == 0 &&
                                                                                    <small className="p-error block m-2">Merchant Currency is required.</small>}
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>Flexcube Branch</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.flexCubeBranch}
                                                                                    required={this.validateInput("flexCubeBranch")}
                                                                                    type="text"
                                                                                    value={this.state.merchant.flexCubeBranch}
                                                                                    onChange={(value) => this.updateValue("flexCubeBranch", value)}
                                                                                    name="FlexCube Branch"
                                                                                    onValidStatusChanged={(value: boolean) =>
                                                                                        this.validationErrors.set("flexCubeBranch", value)
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <Label>Merchant Client</Label>
                                                                            <FormGroup>
                                                                                <MultiSelect
                                                                                    className={`${this.validateDropdown("clientIds")}`}
                                                                                    value={this.state.merchant.clientIds}
                                                                                    options={this.merchantClients}
                                                                                    optionLabel="label"
                                                                                    optionValue="value"
                                                                                    onChange={(e) => this.updateValue("clientIds", e.target.value)}
                                                                                    placeholder={"Select Merchant Clients"}
                                                                                    itemTemplate={(option) => {
                                                                                        return <span>{option.label}</span>;
                                                                                    }}
                                                                                    style={{ width: "100%" }}
                                                                                />
                                                                                {this.state.isSubmit && this.state.merchant.clientIds.length == 0 &&
                                                                                    <small className="p-error block m-2">Merchant Client is required.</small>}
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>
                                                                    <Row>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>BulStat</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.bulStat}
                                                                                    required={this.validateInput("bulStat")}
                                                                                    type="text"
                                                                                    maxLength={BULSTAT_LENGTH}
                                                                                    value={this.state.merchant.bulStat}
                                                                                    onChange={(value) => {
                                                                                        const numericValue = value.replace(NON_DIGIT_REPLACEMENT_REGEX, '').replace(LEADING_ZEROS_REGEX, '');
                                                                                        this.updateValue("bulStat", numericValue);
                                                                                    }}
                                                                                    name="BulStat"
                                                                                    onValidStatusChanged={(value: boolean) =>
                                                                                        this.validationErrors.set("bulStat", value)
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={6}>
                                                                            <FormGroup>
                                                                                <Label>Minimum Transaction Limit</Label>
                                                                                <ExtendedInputText
                                                                                    innerRef={this.inputRef.minimumTransactionAmount}
                                                                                    type="number"
                                                                                    min="0.01"
                                                                                    step="0.01"
                                                                                    value={this.state.merchant.minimumTransactionAmount}
                                                                                    onChange={(value) => this.updateValue("minimumTransactionAmount", value)}
                                                                                    validation={() => (this.state.merchant.minimumTransactionAmount ?? 0) > 0}
                                                                                    name="Minimum Transaction Limit"
                                                                                    onValidStatusChanged={(value: boolean) =>
                                                                                        this.validationErrors.set("minimumTransactionAmount", value)
                                                                                    }
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>
                                                                </Col>
                                                            </Row>
                                                        </AppCard.Body>
                                                    </AppCard>
                            <AppCard>
                                <AppCard.Header title="Merchant Contacts" />
                                                <AppCard.Body>
                                                    <MerchantContactComponent
                                                        merchantContacts={this.state.merchant.merchantContacts}
                                                        merchantId={0}
                                                        localMode={true}
                                                        onLocalContactUpdated={this.onContactsUpdated}
                                                    />
                                </AppCard.Body>
                            </AppCard>
                            <AppCard>
                                <AppCard.Header title="Merchant Addresses" />
                                <AppCard.Body>
                                    <MerchantAddress
                                        merchantAddresses={this.state.merchant.merchantAddresses}
                                        merchantId={0}
                                        localMode={true}
                                        onLocalAddressesUpdated={this.onAddressUpdated}
                                    />
                                </AppCard.Body>
                            </AppCard>
                            <AppCard>
                                <AppCard.Header title="Merchant Documents" />
                                <AppCard.Body>
                                    <MerchantDocuments
                                        merchantDocuments={this.state.merchant.merchantDocuments}
                                        merchantId={0}
                                        localMode={true}
                                        onLocalDocumentUpdated={this.onDocumentsUpdated}
                                        valieDateLocalDocuments={(value) => this.ValidateDocuments(value)}
                                        localDocumentInvalid={this.state.isDocumentInvalid}
                                    />
                                </AppCard.Body>
                            </AppCard>
                            <AppCard>
                                <AppCard.Body>
                                    <div className="flex justify-end px-5 py-4">
                                        <Button
                                            type="button"
                                            disabled={Object.values(this.validationErrors).filter((f) => f == false).length > 0}
                                            label="Submit"
                                            icon={<Check size={16} />}
                                            severity="success"
                                            iconPos="right"
                                            loading={this.state.isLoading}
                                            onClick={this.onSubmitButtonClick}
                                        />
                                    </div>
                                </AppCard.Body>
                            </AppCard>
                        </div>
                    </PageLayout>
                )}
            </>
        );
    }
}
